import { styled } from "@linaria/react";

import { GalaxyTheme } from "../theme/constants";
import { useGalaxyTheme, withTheme } from "../theme/GalaxyTheme";
import type { PropsWithTheme } from "../theme/types";

import { getDarkerHexColor, getLighterHexColor, getOpaqueHexColor } from "../utils/colors";

export interface AvatarEmptyProps {
  size: number;
  hex?: string;
}

export const StyledAvatarEmpty = withTheme(styled.div<
  PropsWithTheme & {
    $size: number;
    $hex?: string;
    $galaxyTheme: Exclude<GalaxyTheme, GalaxyTheme.SYSTEM>;
  }
>`
  width: ${({ $size }) => `${$size}px`};
  height: ${({ $size }) => `${$size}px`};

  background: ${({ theme, $hex, $galaxyTheme }) => {
    const isDark = $galaxyTheme === GalaxyTheme.DARK;
    const base = $hex
      ? $hex
      : isDark
        ? theme.color.border.tertiary
        : getDarkerHexColor(theme.color.border.tertiary, 25);
    const accent = $hex
      ? getLighterHexColor($hex, isDark ? 120 : 130)
      : isDark
        ? getLighterHexColor(theme.color.border.tertiary, 80)
        : getLighterHexColor(theme.color.border.tertiary, 55);
    const galaxy = theme.color.background.galaxy;
    const galaxyRing = getOpaqueHexColor(galaxy, 0.22);
    const galaxyRingSoft = getOpaqueHexColor(galaxy, 0.08);
    return `
      radial-gradient(circle at 50% 50%, transparent 28%, ${galaxyRingSoft} 45%, ${galaxyRing} 62%, ${galaxyRing} 85%, ${galaxyRingSoft} 96%, transparent 100%),
      radial-gradient(ellipse 90% 40% at 30% 25%, ${accent} 0%, transparent 55%),
      radial-gradient(ellipse 85% 35% at 75% 65%, ${accent} 0%, transparent 50%),
      radial-gradient(ellipse 70% 50% at 55% 80%, ${base} 0%, transparent 45%),
      radial-gradient(ellipse 80% 45% at 20% 70%, ${base} 0%, transparent 50%),
      radial-gradient(ellipse 100% 60% at 50% 50%, ${base} 10%, ${accent} 100%)
    `;
  }};

  border-radius: 200px;
`);

const AvatarEmpty = ({ size = 16, hex }: AvatarEmptyProps) => {
  const { galaxyTheme } = useGalaxyTheme();

  return <StyledAvatarEmpty $size={size} $hex={hex} $galaxyTheme={galaxyTheme} />;
};

export default AvatarEmpty;
